import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { ShuffleAttendeesBanner } from "modules/shuffleAttendees/components/ShuffleAttendeesBanner";
import { LobbyBanner } from "modules/lobby/components/LobbyBanner";
import { useShuffleAttendees } from "modules/shuffleAttendees/hooks/useShuffleAttendees";
import {
  selectCurrentEventIsLobbyActive,
  selectEventLogoUrl,
} from "modules/event/selectors";
import {
  selectIsLobbyBannerOpen,
  selectWaitingGuests,
} from "modules/lobby/redux/selectors";
import classNames from "classnames";
import { toggleLobbyBanner } from "modules/lobby/redux/slice";
import { showLobbyNotification } from "modules/lobby/utils/lobbyUtils";
import { selectShowTimer } from "modules/timer/redux/selectors";
import { selectIsMobile } from "modules/deviceInfo";
import { EnforceCountdownMapBanner } from "modules/enforceTableAssignment";
import { usePrevious } from "helpers/reactHooksUtils";
import { selectIsCountdownBannerVisible } from "modules/enforceTableAssignment/redux";
import EventImagePopup from "../EventImagePopup";
import { DefaultView } from "../DefaultView";
import { useStyles } from "./styles";

interface Props {
  hasEventMap: boolean;
}

export const MapBanner = ({ hasEventMap }: Props): JSX.Element => {
  const dispatch = useAppDispatch();
  const showTimerPanel = useSelector(selectShowTimer);
  const [showLargeEventImage, setShowLargeEventImage] = useState(false);
  const eventLogoURL = useSelector(selectEventLogoUrl);
  const isLobbyActive = useSelector(selectCurrentEventIsLobbyActive);
  const isMobileView = useSelector(selectIsMobile);
  const classes = useStyles();
  const { showShuffleAttendeesBanner } = useShuffleAttendees();
  const isEnforceCountdownBannerVisible = useSelector(
    selectIsCountdownBannerVisible,
  );
  const guests = useSelector(selectWaitingGuests);
  const isLobbyBannerOpen = useSelector(selectIsLobbyBannerOpen);
  const waitingGuestsCount = guests?.length ?? 0;
  const prevWaitingCount = usePrevious(waitingGuestsCount);

  const handleHideLargeImage = () => {
    setShowLargeEventImage(false);
  };
  const handleShowLargeImage = () => {
    setShowLargeEventImage((state) => !state);
  };

  useEffect(() => {
    if (prevWaitingCount === undefined) {
      return;
    }

    if (waitingGuestsCount > prevWaitingCount) {
      dispatch(toggleLobbyBanner(true));
      showLobbyNotification(guests);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingGuestsCount, prevWaitingCount]);

  const showLobbyBanner = isLobbyActive && isLobbyBannerOpen && !isMobileView;

  return (
    <>
      <div
        className={classNames(classes.mapBanner, {
          low: showTimerPanel,
        })}
      >
        <DefaultView
          onEventLogoClick={handleShowLargeImage}
          hasEventMap={hasEventMap}
        />
        <div className={classes.notifications}>
          {showShuffleAttendeesBanner && (
            <div className={classes.notificationCtn}>
              <ShuffleAttendeesBanner />
            </div>
          )}
          {isEnforceCountdownBannerVisible && (
            <div className={classes.notificationCtn}>
              <EnforceCountdownMapBanner />
            </div>
          )}
          {showLobbyBanner && (
            <div className={classes.notificationCtn}>
              <LobbyBanner />
            </div>
          )}
        </div>
      </div>
      {eventLogoURL && showLargeEventImage && (
        <EventImagePopup src={eventLogoURL} onClose={handleHideLargeImage} />
      )}
    </>
  );
};
